import axios from 'axios';

/**
 * Robokassa Service для frontend
 * Обрабатывает создание платежей, проверку статуса и редирект
 */

// Статусы платежа Robokassa
export enum RobokassaPaymentStatus {
  PENDING = 'pending',
  PAID = 'paid',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
}

// Ответ от API при создании платежа
export interface RobokassaPaymentResponse {
  id: string;
  order_id: string;
  amount: number;
  currency: string;
  status: RobokassaPaymentStatus;
  description?: string;
  redirect_url: string;
  payment_url: string;
  created_at: string;
  metadata?: Record<string, any>;
  provider_data?: {
    payment_url: string;
    signature: string;
  };
}

// Запрос на создание платежа
export interface CreateRobokassaPaymentRequest {
  order_id: string;
  amount: number;
  currency: string;
  description: string;
  success_url: string;
  failure_url: string;
}

// Параметры из callback URL (после возврата от Robokassa)
export interface RobokassaCallbackParams {
  OutSum: string;
  InvId: string;
  SignatureValue: string;
  Culture?: string;
  Shp_item?: string; // Дополнительные параметры
}

class RobokassaService {
  private baseUrl: string;

  constructor() {
    // Определяем базовый URL из текущего окружения
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '/api';
    this.baseUrl = apiBaseUrl.replace(/\/$/, ''); // Убираем trailing slash
  }

  /**
   * Создает платеж через Robokassa
   */
  async createPayment(request: CreateRobokassaPaymentRequest): Promise<RobokassaPaymentResponse> {
    try {
      const response = await axios.post(`${this.baseUrl}/payments?provider=robokassa`, request, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getToken()}`,
        },
      });

      return {
        id: response.data.data.id,
        order_id: response.data.data.order_id,
        amount: response.data.data.amount,
        currency: response.data.data.currency,
        status: response.data.data.status,
        description: response.data.data.description,
        redirect_url: response.data.redirect_url,
        payment_url: response.data.data.metadata?.payment_url || response.data.redirect_url,
        created_at: response.data.data.created_at,
        metadata: response.data.data.metadata,
        provider_data: response.data.data.provider_data,
      };
    } catch (error) {
      console.error('Robokassa create payment error:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Получает статус платежа
   */
  async getPaymentStatus(paymentId: string): Promise<RobokassaPaymentResponse> {
    try {
      const response = await axios.get(`${this.baseUrl}/payments/${paymentId}?provider=robokassa`, {
        headers: {
          'Authorization': `Bearer ${this.getToken()}`,
        },
      });

      return {
        id: response.data.data.id,
        order_id: response.data.data.order_id,
        amount: response.data.data.amount,
        currency: response.data.data.currency,
        status: response.data.data.status,
        redirect_url: '',
        payment_url: '',
        created_at: response.data.data.created_at,
      };
    } catch (error) {
      console.error('Robokassa get payment status error:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Парсит параметры из URL после возврата от Robokassa
   */
  parseCallbackUrl(): RobokassaCallbackParams | null {
    const params = new URLSearchParams(window.location.search);

    const outSum = params.get('OutSum');
    const invId = params.get('InvId');
    const signatureValue = params.get('SignatureValue');

    if (!outSum || !invId || !signatureValue) {
      return null;
    }

    return {
      OutSum: outSum,
      InvId: invId,
      SignatureValue: signatureValue,
      Culture: params.get('Culture') || undefined,
      Shp_item: params.get('Shp_item') || undefined,
    };
  }

  /**
   * Проверяет, является ли текущий URL страницей возврата от Robokassa
   */
  isRobokassaCallback(): boolean {
    return this.parseCallbackUrl() !== null;
  }

  /**
   * Редирект на страницу оплаты Robokassa
   */
  redirectToPayment(paymentUrl: string): void {
    window.location.href = paymentUrl;
  }

  /**
   * Открывает страницу оплаты в новом окне
   */
  openPaymentWindow(paymentUrl: string): Window | null {
    return window.open(paymentUrl, '_blank', 'width=800,height=600,scrollbars=yes');
  }

  /**
   * Ожидание завершения платежа (polling)
   */
  async pollPaymentStatus(
    paymentId: string,
    intervalMs: number = 3000,
    maxAttempts: number = 60
  ): Promise<RobokassaPaymentResponse> {
    return new Promise((resolve, reject) => {
      let attempts = 0;

      const poll = async () => {
        try {
          attempts++;
          const status = await this.getPaymentStatus(paymentId);

          if (status.status === RobokassaPaymentStatus.PAID) {
            resolve(status);
            return;
          }

          if (status.status === RobokassaPaymentStatus.FAILED ||
              status.status === RobokassaPaymentStatus.CANCELLED) {
            reject(new Error(`Payment ${status.status}`));
            return;
          }

          if (attempts >= maxAttempts) {
            reject(new Error('Payment polling timeout'));
            return;
          }

          setTimeout(poll, intervalMs);
        } catch (error) {
          reject(error);
        }
      };

      poll();
    });
  }

  /**
   * Получает название метода оплаты для отображения
   */
  getPaymentMethodDisplayName(method: string): string {
    const methodNames: Record<string, string> = {
      'card': 'Bank card',
      'bank': 'Bank transfer',
      'wallet': 'E-wallet',
      'crypto': 'Cryptocurrency',
      'other': 'Other method',
    };

    return methodNames[method] || method;
  }

  /**
   * Форматирует сумму
   */
  formatAmount(amount: number, currency: string = 'RUB'): string {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
    }).format(amount);
  }

  /**
   * Получает токен авторизации
   */
  private getToken(): string {
    return localStorage.getItem('auth_token') || '';
  }

  /**
   * Обработка ошибок
   */
  private handleError(error: any): Error {
    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.error || error.message;
      return new Error(`Robokassa: ${message}`);
    }
    return error instanceof Error ? error : new Error('Unknown error');
  }
}

export default new RobokassaService();
